import FlatCard from "components/UI-lib/FlatCard";
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import agent, { Invoice, ListInvoicesResponse } from "server";
import TokenPagination from "components/Graphs/TokenPagination";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { convertUnixEpochToDate } from "./utils";

const INVOICES_PAGE_SIZE = 25;

interface PaymentsProps {
  activeOrgId: string;
}

const toNumber = (value: bigint | number | string | undefined): number => {
  return Number(value ?? 0);
};

const getNextPageToken = (page?: ListInvoicesResponse): string => {
  return page?.next_page_token || "";
};

interface PaymentsPaginationState {
  orgId: string;
  currentPage: number;
  pageTokens: string[];
}

interface PaymentsTableProps {
  currentPage: number;
  hasNextPage: boolean;
  invoices: Invoice[];
  onNextPage: () => void;
  onPreviousPage: () => void;
}

function PaymentsTable(props: PaymentsTableProps) {
  const filteredInvoices = useMemo(() => {
    return props.invoices.filter(
      (i) =>
        i.status !== "void" &&
        i.amount_due !== undefined &&
        toNumber(i.amount_due) > 0,
    );
  }, [props.invoices]);

  const getStatusText = (invoice: Invoice): string => {
    if (invoice.refund !== undefined) {
      return "Refunded";
    }
    if (invoice.status === "paid") {
      return "Paid";
    }
    if (invoice.status === "open") {
      return "Failed";
    }
    return "";
  };

  if (filteredInvoices.length === 0) {
    return (
      <>
        <FlatCard className="w-full justify-center" noBackground>
          <div className="flex w-full justify-center">
            <span className="text-sm text-gray-700 dark:text-secondary">
              No Payment History Found
            </span>
          </div>
        </FlatCard>
        <TokenPagination
          currentPage={props.currentPage}
          hasNextPage={props.hasNextPage}
          onNextPage={props.onNextPage}
          onPreviousPage={props.onPreviousPage}
        />
      </>
    );
  }

  return (
    <>
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell>Amount</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Invoice</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((item, index) => (
              <TableRow key={item.invoice_id || item.id || index}>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary ">
                    {convertUnixEpochToDate(toNumber(item.created))}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {`$${(toNumber(item.amount_due) / 100).toFixed(2)}` || 0}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {getStatusText(item)}
                  </span>
                </TableCell>
                <TableCell>
                  {item.status === "paid" && item.invoice_url && (
                    <a href={item.invoice_url}>
                      <span className="text-highlight underline">View</span>
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableRoot>
      <TokenPagination
        currentPage={props.currentPage}
        hasNextPage={props.hasNextPage}
        onNextPage={props.onNextPage}
        onPreviousPage={props.onPreviousPage}
      />
    </>
  );
}

const Payments: React.FC<PaymentsProps> = ({ activeOrgId }) => {
  const [paginationState, setPaginationState] =
    useState<PaymentsPaginationState>({
      orgId: activeOrgId,
      currentPage: 1,
      pageTokens: [""],
    });
  const pagination =
    paginationState.orgId === activeOrgId
      ? paginationState
      : { orgId: activeOrgId, currentPage: 1, pageTokens: [""] };
  const { currentPage, pageTokens } = pagination;
  const pageToken = pageTokens[currentPage - 1] ?? "";

  const {
    data: invoicePage,
    isLoading: invoicesLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["invoices", activeOrgId, pageToken],
    queryFn: async () => {
      const response = await agent.Organizations.getInvoices(activeOrgId, {
        pageSize: INVOICES_PAGE_SIZE,
        pageToken,
      });
      return response.data ?? {};
    },
    enabled: !!activeOrgId,
  });

  const handlePreviousPage = () => {
    setPaginationState({
      orgId: activeOrgId,
      currentPage: Math.max(1, currentPage - 1),
      pageTokens,
    });
  };

  const handleNextPage = () => {
    const nextPageToken = getNextPageToken(invoicePage);
    if (!nextPageToken) return;

    setPaginationState({
      orgId: activeOrgId,
      currentPage: currentPage + 1,
      pageTokens: (() => {
        const nextTokens = pageTokens.slice(0, currentPage);
        nextTokens[currentPage] = nextPageToken;
        return nextTokens;
      })(),
    });
  };

  const showLoading = !!activeOrgId && invoicesLoading;

  return (
    <FlatCard className="w-full" noBorder={true} noBackground>
      <h1 className="text-xl font-medium dark:text-white mb-1">Payments</h1>
      <div>
        <p className="text-sm text-gray-700 dark:text-secondary">
          Review your payment history
        </p>
      </div>
      <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
      {showLoading ? (
        <FlatCard isLoading={true} noBackground>
          <div className="flex w-full justify-center">
            <span className="text-sm text-gray-700 dark:text-secondary">
              Fetching invoices
            </span>
          </div>
        </FlatCard>
      ) : isError ? (
        <FlatCard className="w-full justify-center" noBackground>
          <div className="flex w-full justify-center">
            <span className="text-sm text-gray-700 dark:text-secondary">
              {error instanceof Error
                ? error.message
                : "Failed to fetch invoices"}
            </span>
          </div>
        </FlatCard>
      ) : (
        <PaymentsTable
          currentPage={currentPage}
          hasNextPage={!!getNextPageToken(invoicePage)}
          invoices={invoicePage?.invoices ?? []}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
        />
      )}
    </FlatCard>
  );
};

export default Payments;
